import { RenderWhen } from 'legacy/lut/components/shared/renderWnen/RenderWhen'
import { RowsControl } from 'legacy/lut/components/widgets/public/partner360/ELT/ui/styledComponents/rowsControl/RowsControl'
import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import { Translation } from 'cng-common/features/translations'
import { TranslationMode } from 'cng-common/features/translations/configs'
import { selectTranslationMode } from 'cng-common/features/translations/selectors'
import { Input, Textarea } from 'core-components/library/inputs'

import styles from './MultiInputsControl.module.scss'

interface IProps {
  values: string[]
  onChange: (values: string[]) => void
  placeholder?: string
  isTranslatable?: boolean
  maxLen?: number
  minLen?: number
  showBottomControl?: boolean
  showRowControl?: boolean
  isTextArea?: boolean
  withDot?: boolean
}

export const MultiInputsControl = ({
  values,
  onChange,
  placeholder,
  isTranslatable = false,
  maxLen = 5,
  minLen,
  showBottomControl = true,
  showRowControl = false,
  isTextArea,
  withDot,
}: IProps) => {
  const [t] = useTranslation()

  const placeholderText = useMemo(() => {
    if (isTranslatable) {
      return t(placeholder || '')
    }
    return placeholder
  }, [placeholder, isTranslatable, t])

  const handleDotChange = useCallback(
    (values: string[]) => {
      if (withDot) {
        const updated = values.map((el) => {
          if (el.startsWith('•')) {
            return el
          }
          return `• ${el}`
        })
        onChange(updated)
        return
      }
      onChange(values)
    },
    [onChange, withDot]
  )

  const handleChange = useCallback(
    (index: number) => (value: string) => {
      const newValues = [...values]
      newValues[index] = value
      handleDotChange(newValues)
    },
    [values, onChange]
  )

  const addRow = useCallback(() => {
    handleDotChange([...values, ''])
  }, [values, onChange])

  const deleteRow = useCallback(
    (index: number) => {
      const clonedValues = structuredClone(values)
      clonedValues.splice(index, 1)
      if (!clonedValues.length) {
        clonedValues.push('')
      }
      handleDotChange(clonedValues)
    },
    [values, handleDotChange]
  )

  const deleteLastRow = useCallback(() => {
    handleDotChange(values.slice(0, -1))
  }, [values, onChange])

  const canAddNewRow = useMemo(() => {
    const emptyRows = values.filter((el) => !el)
    return emptyRows.length === 0
  }, [values])

  return (
    <div className={styles.root}>
      {values.map((value, index) => {
        const showAddNewRow = values.length === index + 1

        if (isTextArea) {
          return (
            <div className={styles.inlineControls} key={index}>
              <textarea
                value={value}
                placeholder={placeholderText}
                className={styles.textarea}
                onChange={(e) => handleChange(index)(e.target.value)}
                rows={value.split('\n').length}
              />
              <RenderWhen condition={showRowControl}>
                <RowsControl
                  containerClassName={styles.column}
                  addRow={addRow}
                  deleteRow={() => deleteRow(index)}
                  maxLen={maxLen}
                  minLen={minLen}
                  items={values}
                  canAddNewRow={canAddNewRow}
                  showAddNewRow={showAddNewRow}
                />
              </RenderWhen>
            </div>
          )
        }

        return (
          <Input
            classes={{
              root: styles.input,
            }}
            key={index}
            value={value}
            onChange={handleChange(index)}
            placeholder={t(placeholder || '').replace(/%20/g, ' ')}
          />
        )
      })}

      <RenderWhen condition={showBottomControl}>
        <RowsControl
          addRow={addRow}
          deleteRow={deleteLastRow}
          maxLen={maxLen}
          minLen={minLen}
          items={values}
          canAddNewRow={canAddNewRow}
        />
      </RenderWhen>
    </div>
  )
}
